import {Box} from '@dropbox/dig-foundations';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createEmojiNode} from 'components/DSYS/editor/nodes/ImageNode';
import {SearchIndex} from 'emoji-mart';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {$getSelection, $isRangeSelection} from 'lexical';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {Emoji, EmojiPicker, getEmojiImageUrl} from './EmojiPicker';

export const EmojiPickerPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [showPicker, setShowPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [position, setPosition] = useState({top: 0, left: 0});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [enterPressed, setEnterPressed] = useState(0); // Counter to trigger selection
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<{node: any; offset: number} | null>(null);
  const positionLocked = useRef(false); // Lock position after first set

  const insertEmoji = useCallback(
    (emoji: Emoji) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && triggerRef.current) {
          const anchor = selection.anchor;
          const textNode = anchor.getNode();
          const textContent = textNode.getTextContent();
          const anchorOffset = anchor.offset;

          // Find the last : before the cursor
          let colonIndex = -1;
          for (let i = anchorOffset - 1; i >= 0; i--) {
            if (textContent[i] === ':') {
              colonIndex = i;
              break;
            }
          }

          if (colonIndex !== -1) {
            // Select from colon to cursor position
            selection.anchor.offset = colonIndex;
            selection.focus.offset = anchorOffset;

            // Delete the colon and search text
            selection.insertText('');

            // Insert emoji image with space
            const imageNode = $createEmojiNode({
              src: getEmojiImageUrl(emoji),
              altText: emoji.name,
            });
            selection.insertNodes([imageNode]);
            selection.insertText(' ');
          }
        }
      });
      setShowPicker(false);
      setSearchQuery('');
      setSelectedIndex(0);
      triggerRef.current = null;
      positionLocked.current = false;
    },
    [editor]
  );

  // Handle click outside to close picker
  useOutsideClick(
    pickerRef,
    () => {
      setShowPicker(false);
      setSearchQuery('');
      setSelectedIndex(0);
      triggerRef.current = null;
      positionLocked.current = false;
    },
    [],
    false,
    'mousedown'
  );

  // Handle arrow keys and enter for emoji selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showPicker) return;

      if (event.key === 'Escape') {
        setShowPicker(false);
        setSearchQuery('');
        setSelectedIndex(0);
        triggerRef.current = null;
        positionLocked.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => prev + 7); // Move down one row (7 columns)
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => Math.max(0, prev - 7)); // Move up one row (7 columns)
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'ArrowLeft') {
        setSelectedIndex((prev) => Math.max(0, prev - 1)); // Move left one emoji
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'ArrowRight') {
        setSelectedIndex((prev) => prev + 1); // Move right one emoji
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        // Trigger emoji selection by incrementing counter
        setEnterPressed((prev) => prev + 1);
        return;
      }
    };

    if (showPicker) {
      document.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [showPicker, selectedIndex]);

  // Handle pasting emoji characters and convert them to emoji images

  useEffect(() => {
    return editor.registerUpdateListener(({editorState}) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          if (showPicker) {
            setShowPicker(false);
            setSearchQuery('');
          }
          return;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        const anchorOffset = anchor.offset;

        // Get text before cursor
        const textContent = anchorNode.getTextContent();
        const textBeforeCursor = textContent.slice(0, anchorOffset);

        // Check for completed emoji shortcode pattern :word:
        // Must have space, opening paren before : or be at start of text
        const completedEmojiMatch = textBeforeCursor.match(/(^|\s|\()(:([a-zA-Z0-9_+-]+):)$/);

        if (completedEmojiMatch) {
          const emojiName = completedEmojiMatch[3];
          const fullMatch = completedEmojiMatch[2]; // e.g., ":pizza:"
          const prefix = completedEmojiMatch[1]; // space or empty string (if at start)

          // Search for the emoji
          SearchIndex.search(emojiName).then((results) => {
            if (results && results.length > 0) {
              // Find exact match (case insensitive)
              const exactMatch = results.find(
                (emoji: Emoji) =>
                  emoji.id === emojiName.toLowerCase() ||
                  emoji.name.toLowerCase() === emojiName.toLowerCase() ||
                  (emoji.keywords &&
                    emoji.keywords.some(
                      (kw: string) => kw.toLowerCase() === emojiName.toLowerCase()
                    ))
              );

              const emojiToInsert = exactMatch || results[0];

              // Auto-insert the emoji
              editor.update(() => {
                const sel = $getSelection();
                if ($isRangeSelection(sel)) {
                  const offset = sel.anchor.offset;

                  // Calculate where the emoji shortcode starts (including prefix space if present)
                  const startIndex = offset - fullMatch.length - prefix.length;

                  if (startIndex >= 0) {
                    // Select from start (after space) to cursor
                    sel.anchor.offset = startIndex + prefix.length;
                    sel.focus.offset = offset;

                    // Delete the shortcode
                    sel.insertText('');

                    // Insert emoji image with space
                    const imageNode = $createEmojiNode({
                      src: getEmojiImageUrl(emojiToInsert),
                      altText: emojiToInsert.name,
                    });
                    sel.insertNodes([imageNode]);
                    sel.insertText(' ');
                  }
                }
              });

              // Close picker if it's open
              if (showPicker) {
                setShowPicker(false);
                setSearchQuery('');
                triggerRef.current = null;
                positionLocked.current = false;
              }

              return;
            }
          });
        }

        // Check for : trigger (incomplete emoji shortcode)
        // Must have space, opening paren before : or be at start of text
        const colonMatch = textBeforeCursor.match(/(^|\s|\()(:([a-zA-Z0-9_+-]*))$/);

        if (colonMatch) {
          const query = colonMatch[3];

          // Only set position when picker first opens
          if (!showPicker) {
            // Get cursor position for picker placement
            const domSelection = window.getSelection();
            if (domSelection && domSelection.rangeCount > 0) {
              const range = domSelection.getRangeAt(0);
              const rect = range.getBoundingClientRect();

              setPosition({
                top: rect.bottom + window.scrollY + 5,
                left: rect.left + window.scrollX,
              });
              positionLocked.current = true;
            }

            triggerRef.current = {node: anchorNode, offset: anchorOffset};
            setShowPicker(true);
          }

          setSearchQuery(query);
        } else if (showPicker) {
          setShowPicker(false);
          setSearchQuery('');
          triggerRef.current = null;
          positionLocked.current = false;
        }
      });
    });
  }, [editor, showPicker]);

  if (!showPicker) {
    return null;
  }

  return createPortal(
    <Box
      ref={pickerRef}
      boxShadow="Floating"
      borderRadius="Medium"
      overflow="hidden"
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000,
      }}
    >
      <EmojiPicker
        searchQuery={searchQuery}
        selectedIndex={selectedIndex}
        onEmojiSelect={insertEmoji}
        onSelectedIndexChange={setSelectedIndex}
        onEnterPress={enterPressed}
      />
    </Box>,
    document.body
  );
};
